/**
 * Sources per month, stacked by kind of source, with the alerts raised that month as a line on
 * the same count axis (PRD-0075 R-D2). A table view carries the same numbers for readers who
 * cannot tell the fills apart, and for print.
 */
import { BarChart3, Table2 } from 'lucide-react'
import { type JSX, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  type BarShapeProps,
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ChartContainer } from '@/components/ui/chart'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import type { SourceKind } from '@/services/api/case-dashboard'
import {
  ALERT_LINE_COLOR,
  axisMonths,
  type DashboardMonthView,
  formatCount,
  formatMonth,
  monthTickLines,
  SOURCE_KIND_COLOR,
} from './dashboard-view'

type ChartRow = DashboardMonthView & {
  top: SourceKind | null
  /** The alerts line: it starts the month before the first alert, so it never runs along the axis for years. */
  alertLine: number | null
}

const AXIS_TICK = { fill: 'var(--color-muted-foreground)', fontSize: 11 }

interface MonthTickProps {
  x?: number
  y?: number
  payload?: { value?: string }
  first?: string
}

/** The month under its bar; January and the first month carry the year on a second line. */
function MonthTick({ x = 0, y = 0, payload, first }: MonthTickProps) {
  const { i18n } = useTranslation()
  const month = payload?.value
  if (!month) return null
  const lines = monthTickLines(month, i18n.language, month === first)
  return (
    <text x={x} y={y + 12} textAnchor="middle" {...AXIS_TICK}>
      {lines.map((line, i) => (
        <tspan key={line} x={x} dy={i === 0 ? 0 : 13}>
          {line}
        </tspan>
      ))}
    </text>
  )
}

interface AlertDotProps {
  cx?: number
  cy?: number
  value?: number
  index?: number
}

/** A marker only where alerts were raised: a zero month is the baseline, not a data point. */
function AlertDot({ cx, cy, value, index }: AlertDotProps) {
  if (!value || cx == null || cy == null) return <g key={index} />
  return (
    <circle
      key={index}
      cx={cx}
      cy={cy}
      r={4}
      fill={ALERT_LINE_COLOR}
      stroke="var(--color-card)"
      strokeWidth={2}
    />
  )
}

/** The topmost non-empty segment of a month: only it gets the rounded data-end. */
function topKind(month: DashboardMonthView, kinds: readonly SourceKind[]) {
  return [...kinds].reverse().find(kind => month.sources[kind] > 0) ?? null
}

const SEGMENT_SHAPES = new Map<SourceKind, (props: BarShapeProps) => JSX.Element>()

/** One stable shape per kind, so a re-render does not hand recharts new functions. */
function segmentShape(kind: SourceKind) {
  let shape = SEGMENT_SHAPES.get(kind)
  if (!shape) {
    shape = function Segment(props: BarShapeProps) {
      const top = (props.payload as ChartRow | undefined)?.top === kind
      return <Rectangle {...props} radius={top ? [4, 4, 0, 0] : 0} />
    }
    SEGMENT_SHAPES.set(kind, shape)
  }
  return shape
}

function Swatch({ color, line = false }: { color: string; line?: boolean }) {
  return (
    <span
      aria-hidden
      className={line ? 'h-0.5 w-3.5 rounded-full' : 'size-2.5 rounded-sm'}
      style={{ background: color }}
    />
  )
}

function Legend({ kinds }: { kinds: readonly SourceKind[] }) {
  const { t } = useTranslation()
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
      {kinds.map(kind => (
        <li key={kind} className="flex items-center gap-1.5">
          <Swatch color={SOURCE_KIND_COLOR[kind]} />
          {t(`caseDashboard.sourceKinds.${kind}`)}
        </li>
      ))}
      <li className="flex items-center gap-1.5">
        <Swatch color={ALERT_LINE_COLOR} line />
        {t('caseDashboard.chart.alerts')}
      </li>
    </ul>
  )
}

interface MonthTooltipProps {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: unknown }>
  kinds: readonly SourceKind[]
}

function MonthTooltip({ active, payload, kinds }: MonthTooltipProps) {
  const { t, i18n } = useTranslation()
  const row = payload?.[0]?.payload as ChartRow | undefined
  if (!active || !row) return null
  const lines = kinds.filter(kind => row.sources[kind] > 0)
  return (
    <div className="min-w-40 rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-lg">
      <p className="mb-1.5 font-medium text-popover-foreground">
        {formatMonth(row.month, i18n.language)}
      </p>
      {lines.map(kind => (
        <p key={kind} className="flex items-center gap-2 text-muted-foreground">
          <Swatch color={SOURCE_KIND_COLOR[kind]} />
          <span className="flex-1">{t(`caseDashboard.sourceKinds.${kind}`)}</span>
          <span className="text-popover-foreground">
            {formatCount(row.sources[kind], i18n.language)}
          </span>
        </p>
      ))}
      <p className="mt-1 flex items-center gap-2 border-t border-border pt-1 text-muted-foreground">
        <Swatch color={ALERT_LINE_COLOR} line />
        <span className="flex-1">{t('caseDashboard.chart.alerts')}</span>
        <span className="text-popover-foreground">{formatCount(row.alerts, i18n.language)}</span>
      </p>
    </div>
  )
}

function MonthsChart({ rows, kinds }: { rows: readonly ChartRow[]; kinds: readonly SourceKind[] }) {
  const { t } = useTranslation()
  const ticks = axisMonths(rows.map(row => row.month))
  return (
    <div role="img" aria-label={t('caseDashboard.chart.title')}>
      <ChartContainer height={260}>
        <ComposedChart
          data={rows as ChartRow[]}
          margin={{ top: 8, right: 8, bottom: 0, left: -12 }}
        >
          <CartesianGrid vertical={false} stroke="var(--color-border)" />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={{ stroke: 'var(--color-border)' }}
            ticks={ticks}
            interval={0}
            tick={<MonthTick first={ticks[0]} />}
            height={36}
          />
          <YAxis
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tick={AXIS_TICK}
            width={40}
          />
          <Tooltip
            cursor={{ fill: 'var(--color-muted)', opacity: 0.6 }}
            content={<MonthTooltip kinds={kinds} />}
          />
          {kinds.map(kind => (
            <Bar
              key={kind}
              dataKey={`sources.${kind}`}
              stackId="sources"
              fill={SOURCE_KIND_COLOR[kind]}
              stroke="var(--color-card)"
              strokeWidth={2}
              maxBarSize={28}
              isAnimationActive={false}
              shape={segmentShape(kind)}
            />
          ))}
          <Line
            dataKey="alertLine"
            type="linear"
            stroke={ALERT_LINE_COLOR}
            strokeWidth={2}
            dot={AlertDot}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ChartContainer>
    </div>
  )
}

function MonthsTable({ rows, kinds }: { rows: readonly ChartRow[]; kinds: readonly SourceKind[] }) {
  const { t, i18n } = useTranslation()
  const count = (value: number) => formatCount(value, i18n.language)
  return (
    <div className="max-h-72 overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('caseDashboard.chart.month')}</TableHead>
            {kinds.map(kind => (
              <TableHead key={kind} className="text-right">
                {t(`caseDashboard.sourceKinds.${kind}`)}
              </TableHead>
            ))}
            <TableHead className="text-right">{t('caseDashboard.chart.total')}</TableHead>
            <TableHead className="text-right">{t('caseDashboard.chart.alerts')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...rows].reverse().map(row => (
            <TableRow key={row.month}>
              <TableCell>{formatMonth(row.month, i18n.language)}</TableCell>
              {kinds.map(kind => (
                <TableCell key={kind} className="text-right tabular-nums">
                  {count(row.sources[kind])}
                </TableCell>
              ))}
              <TableCell className="text-right font-medium tabular-nums">
                {count(kinds.reduce((sum, kind) => sum + row.sources[kind], 0))}
              </TableCell>
              <TableCell className="text-right tabular-nums">{count(row.alerts)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

type Mode = 'chart' | 'table'

export interface SourcesPerMonthChartProps {
  months: readonly DashboardMonthView[]
  /** The stack, bottom first. */
  sourceKinds: readonly SourceKind[]
}

export function SourcesPerMonthChart({ months, sourceKinds }: SourcesPerMonthChartProps) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<Mode>('chart')
  // A kind keeps its colour; the legend and the table name only the kinds the file has.
  const kinds = useMemo(
    () => sourceKinds.filter(kind => months.some(month => month.sources[kind] > 0)),
    [months, sourceKinds]
  )
  const rows = useMemo(() => {
    const firstAlert = months.findIndex(month => month.alerts > 0)
    return months.map((month, i) => ({
      ...month,
      top: topKind(month, kinds),
      alertLine: firstAlert >= 0 && i >= firstAlert - 1 ? month.alerts : null,
    }))
  }, [months, kinds])
  const empty = kinds.length === 0 && months.every(month => month.alerts === 0)

  return (
    <section className="flex flex-col gap-4" aria-labelledby="case-dashboard-chart-title">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 id="case-dashboard-chart-title" className="text-sm font-semibold text-foreground">
            {t('caseDashboard.chart.title')}
          </h3>
          <p className="text-xs text-muted-foreground">{t('caseDashboard.chart.description')}</p>
        </div>
        {!empty && (
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={mode}
            onValueChange={value => value && setMode(value as Mode)}
          >
            <ToggleGroupItem value="chart" aria-label={t('caseDashboard.chart.showChart')}>
              <BarChart3 className="size-3.5" />
              {t('caseDashboard.chart.showChart')}
            </ToggleGroupItem>
            <ToggleGroupItem value="table" aria-label={t('caseDashboard.chart.showTable')}>
              <Table2 className="size-3.5" />
              {t('caseDashboard.chart.showTable')}
            </ToggleGroupItem>
          </ToggleGroup>
        )}
      </header>
      {empty ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          {t('caseDashboard.chart.empty')}
        </p>
      ) : (
        <>
          <Legend kinds={kinds} />
          {mode === 'chart' ? (
            <MonthsChart rows={rows} kinds={kinds} />
          ) : (
            <MonthsTable rows={rows} kinds={kinds} />
          )}
        </>
      )}
    </section>
  )
}
